<template>
  <v-app>
    <v-theme-provider root>
      <div
        class="fill-height"
        @mousemove="resizeDrawer"
        @mouseup="resizeDrawerEnd"
      >
        <Header v-if="user" />

        <Navigation
          v-if="user"
          ref="navDrawer"
        />

        <v-main
          :class="`fill-height ${inDarkMode ? 'black' : 'icewhite'}`"
        >
          <Auth v-if="showSignIn" />

          <router-view
            v-else-if="user && currentRouteName"
            class="fill-height"
          />

          <v-card
            v-else
            class="ma-3"
          >
            <v-card-text>
              <span class="mr-1">404</span>
              <span>Not Found</span>
            </v-card-text>
          </v-card>

          <error-card
            v-if="promptError"
            class="ma-3"
          />

          <v-snackbar
            v-model="notification"
          >
            <v-row>
              <v-col class="align-center d-flex">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="snack.message" />
              </v-col>

              <v-col class="text-right">
                <v-btn
                  color="primary"
                  text
                  @click="fireActionEvent(snack)"
                >
                  Close
                </v-btn>
              </v-col>
            </v-row>
          </v-snackbar>

          <feature-modal />
          <settings-modal />
          <refresh-modal />
        </v-main>
      </div>
    </v-theme-provider>
  </v-app>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import PATHS from 'src/router/paths'

import { fireanalytics } from 'src/store/firebase'

const {
  mapGetters: mapAuthGetters,
  mapState: mapAuthState,
} = createNamespacedHelpers('auth')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Layout',
  components: {
    Auth: () => import('src/auth/views/Auth'),
    ErrorCard: () => import('../components/ErrorCard'),
    Header: () => import('../components/Header'),
    Navigation: () => import('../components/Navigation'),
    FeatureModal: () => import('../components/FeatureModal'),
    RefreshModal: () => import('../components/RefreshModal'),
    SettingsModal: () => import('../components/SettingsModal'),
  },
  computed: {
    ...mapAuthGetters(['isActiveUser', 'inDarkMode']),
    ...mapAuthState({
      demoMode: (state) => state.demoMode,
      isAutoAuthing: (state) => state.isAutoAuthing,
      isAuthCheckRequested: (state) => state.isAuthCheckRequested,
      isSigningIn: (state) => state.isSigningIn,
      user: (state) => state.user,
    }),
    ...mapLayoutState({
      drawerDefaults: (state) => state.drawerDefaults,
      drawerDragStartX: (state) => state.drawerDragStartX,
      drawerDragStartWidth: (state) => state.drawerDragStartWidth,
      drawerWidth: (state) => state.drawerWidth,
      isResizingNavigation: (state) => state.isResizingNavigation,

      promptError: (state) => state.promptError,
    }),
    ...mapToolsGetters([
      'currentRouteName',
      'publishersAsTeams',
      'persons',
      'projects',
      'selectedSearch',
    ]),
    ...mapToolsState({
      adjuster: (state) => state.adjuster,
      reportPath: (state) => state.reportPath,
      associationId: (state) => state.associationId,
      baseReportName: (state) => state.baseReportName,
      routeName: (state) => state.routeName,
      searchResults: (state) => state.searchResults,
      snack: (state) => state.snack,
      snackbar: (state) => state.snackbar,
      subId: (state) => state.subId,
    }),
    notification: {
      get() {
        return this.snackbar
      },
      set(value) {
        this.$store.commit('tools/snackbar', value, { root: true })
      },
    },
    showSignIn() {
      return !this.isActiveUser && !this.isSigningIn
    },
    customAnalyticOptions() {
      return this.searchResults.endpoints['custom-analytics']?.items?.list || []
    },
    currentPageKey() {
      return this.baseReportName || this.routeName
    },

    isDraggingNavigation: {
      get() {
        return this.isResizingNavigation
      },
      set(value) {
        this.$store.commit('layout/isResizingNavigation', value)
      },
    },
    newDrawerDragStartWidth: {
      get() {
        return this.drawerDragStartWidth
      },
      set(value) {
        this.$store.commit('layout/drawerDragStartWidth', value)
      },
    },
    newDragStartX: {
      get() {
        return this.drawerDragStartX
      },
      set(value) {
        this.$store.commit('layout/drawerDragStartX', value)
      },
    },
    newDrawerWidth: {
      get() {
        return this.drawerWidth
      },
      set(value) {
        this.$store.commit('layout/drawerWidth', value)
      },
    },
  },
  watch: {
    $route(to, from) {
      this.setup(to, from || {})
    },
    isActiveUser() {
      const { query } = this.$route
      const { baseReportName, routeName } = this
      const to = { params: { baseReportName, routeName }, query }
      const from = { params: { baseReportName, routeName }, query }

      this.setup(to, from || {})
    },
    inDarkMode(newValue) {
      this.setDarkMode(newValue)
    },
  },
  created() {
    this.setDarkMode()
  },
  methods: {
    setDarkMode(value) {
      this.$vuetify.theme.dark = value || this.inDarkMode
    },

    fireActionEvent(event) {
      if (event && event.action) {
        this.$store.dispatch(event.action)
      } else {
        this.notification = false
      }
    },

    setup(to, from) {
      const { name, params: toParams, query: toQuery } = to
      const params = {
        baseReportName: toParams.baseReportName,
        routeName: toParams.routeName || name,
      }
      const query = {
        ...toQuery,
        associationId: toQuery.associationId || toParams.associationId,
        reportPath: toQuery.reportPath || toParams.reportPath,
        subId: toQuery.subId || toParams.subId,
      }

      this.$store.dispatch('tools/setup', { params, query }, { root: true })

      this.setTitle(to, from)
    },

    makePageTitle(titleKey, titleName) {
      const titleMap = {
        dashboard: (name) => `${name || 'Default'} | Dashboard`,
        docs: () => 'User Docs',
        home: () => 'Home',

        discrepancy: (name) => `${name || 'Default'} | Discrepancy`,
        pacing: (name) => `${name || 'Default'} | Pacing`,

        [PATHS.PROGRAMMATIC.ADX_HEALTH]: (name) => `${name || 'Default'} | AdX Health`,
        [PATHS.PROGRAMMATIC.NETWORK_HEALTH]: (name) => `${name || 'Default'} | Network Health`,
        [PATHS.PROGRAMMATIC.ADX_AND_OPEN_BIDDING]: (name) => `${name || 'Default'} | AdX & OB`,

        [PATHS.INTERNAL.TIME_ROLLUP]: (name) => `${name || 'All'} | Time Rollup`,
        [PATHS.INTERNAL.TIME_SHEET]: (name) => `${name || 'All'} | Time Sheet`,

        organization: (name) => `${name || 'All'} | Org`,
        person: (name) => `${name || 'All'} | Person`,
        project: (name) => `${name || 'All'} | Project`,
      }

      return typeof titleMap[titleKey] === 'function' && titleMap[titleKey](titleName)
    },
    makeCustomAnalyticTitle(baseReportName, foundItem) {
      const isCustomAnalyticRoute = this.$route.name === 'custom-analytics'
      const findCustomAnalyticConfig = () => {
        const findCustomAnalytic = (foundOrg) => {
          const customAnalyticPermissions = foundOrg && foundOrg.access.permissions.customAnalytics
          return Array.isArray(customAnalyticPermissions) && customAnalyticPermissions.find((customAnalytic) => customAnalytic.href === baseReportName)
        }
        return isCustomAnalyticRoute && findCustomAnalytic(foundItem)
      }
      const getCustomAnalyticTitle = (customAnalytic) => {
        const getPageTitleForList = () => !customAnalytic && 'All | Custom Analytic'
        const getPageTitleForReport = () => customAnalytic && customAnalytic.name && `${customAnalytic.name} | Custom Analytic`
        return isCustomAnalyticRoute && (getPageTitleForList() || getPageTitleForReport() || 'Unknown | Custom Analytic')
      }

      return getCustomAnalyticTitle(findCustomAnalyticConfig())
    },

    logPageAnalytics(page_title, fromTitle, toTitle) {
      const { location: { href: page_location, pathname: page_path } } = document

      fireanalytics.event('page_view', {
        page_location,
        page_path,
        page_title,
      })

      fireanalytics.event('navigation', {
        category: 'Navigation',
        description: 'Update Route',
        label: `Update Route from "${fromTitle}" to "${toTitle}"`,
        pageLocation: page_location,
        pagePath: page_path,
        pageTitle: page_title,
      })
    },
    setAssociationId(assocId, sId) {
      return (assocId && ['-1', 'all-secondary'].indexOf(sId) === -1 && `${assocId}/${sId}`) || `${assocId}`
    },
    getPageTitle(endpointKey, reportCode, associationId, subId) {
      const { currentPageKey } = this

      const modelMap = {
        publishersAsTeams: {
          comparator: (org) => org.billingCode === associationId && org.orgId === subId,
          identifier: 'orgName',
          model: this.publishersAsTeams,
        },
        person: {
          comparator: (person) => person.associationId === associationId,
          identifier: 'personName',
          model: this.persons,
        },
        project: {
          comparator: (project) => project.associationId === associationId,
          identifier: 'projectName',
          model: this.projects,
        },
      }
      const currentModelMap = (endpointKey && modelMap[endpointKey]) || modelMap.publishersAsTeams
      const { comparator, identifier, model } = currentModelMap
      const foundItem = model && (model.items || model).find(comparator)
      const identifierForPageTitle = (foundItem && foundItem[identifier]) || reportCode
      const makePageTitle = (pageId) => this.makePageTitle(currentPageKey, pageId)

      return makePageTitle(identifierForPageTitle) || this.makeCustomAnalyticTitle(endpointKey, foundItem) || '404 Not Found'
    },
    setTitle(to) {
      const { isActiveUser } = this
      const toParams = to?.params || this
      const toQuery = to?.query || this
      const currentPageTitle = document.title

      const { baseReportName: toBaseReportName, routeName: toRouteName } = toParams
      const { associationId: toAssociationId, reportPath: toReportPath, subId: toSubId } = toQuery
      const endpointKey = toBaseReportName || toRouteName
      const reportCode = (toAssociationId && this.setAssociationId(toAssociationId, toSubId)) || ''

      const page_title = (isActiveUser && this.getPageTitle(endpointKey, reportCode, toAssociationId, toSubId, toReportPath)) || 'Sign In'

      this.logPageAnalytics(page_title, currentPageTitle)

      document.title = page_title
    },

    resizeDrawer(event) {
      const updateDrawerWidth = () => {
        const { drawerDefaults, drawerDragStartWidth, drawerDragStartX } = this
        const { min: minWidth, max: maxWidth } = drawerDefaults

        const currentX = event.pageX
        const dragDistance = currentX - drawerDragStartX
        const setWidth = dragDistance + drawerDragStartWidth

        const getMaxWidth = () => setWidth > maxWidth && maxWidth
        const getMinWidth = () => setWidth < minWidth && minWidth
        const newWidth = getMaxWidth() || getMinWidth() || setWidth

        this.newDrawerWidth = newWidth

        return true
      }

      return this.isDraggingNavigation && updateDrawerWidth()
    },
    resizeDrawerEnd(event) {
      const resize = this.resizeDrawer(event)

      if (resize) {
        this.isDraggingNavigation = false
        this.newDragStartX = undefined
        this.newDrawerDragStartWidth = this.drawerWidth

        this.$refs.navDrawer.$el.style.transition = 'none'
      }
    },
  },
}
</script>
